import {useContext, useEffect, useMemo, useState} from 'react';

import dayjs from 'dayjs';
import {FormikProvider, useFormik} from 'formik';
import {useTranslation} from 'react-i18next';

import {Box} from '@mui/material';

import {Calendar} from './Calendar';
import {ModalHeader} from './ModalHeader';
import {SurveyOptions} from './SurveyOptions';
import {ReactComponent as EmpionLogo} from '../../../../../../assets/survey_managment/empion_logo_small_white.svg';
import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import {LoadingContext} from '../../../../../../contexts/LoadingContext/LoadingContext';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {useSurvey} from '../../../../../../contexts/SurveyContext/SurveyContext';
import {UserContext} from '../../../../../../contexts/UserContext/UserContext';
import {greenColors} from '../../../../../../layouts/theme/designSystem';
import {CompanyAdminRoleData} from '../../../../../../types/User';
import {SurveyService} from '../../services/SurveyService';
import {StartSurveyPayload, Survey} from '../../types/SurveyTypes';

const MINIMUM_EXPECTED_PARTICIPANTS = 2;

const SURVEY_START_API_ERRORS = {
  noLocations: 'Can\u0027t start a survey without company locations',
  noDepartments: 'Can\u0027t start a survey without company departments',
  noBenefits: 'Can\u0027t start a survey without company benefits',
} as const;

const formatSurveyDate = (date: dayjs.Dayjs) => date.startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]');

interface SurveyManagmentModalProps {
  handleClose(): void;
  type: string;
  state: string | null;
  handleShowSurveyDetails?(state: string | null): Survey | undefined;
}

export const SurveyManagmentModal = ({
  handleClose,
  type,
  state,
  handleShowSurveyDetails,
}: SurveyManagmentModalProps) => {
  const {t} = useTranslation();
  const {currentUser} = useContext(UserContext);
  const {fetchSurveys} = useSurvey();
  const companyId = (currentUser?.role?.data as CompanyAdminRoleData)?.companyId;
  const {setLoadingContext} = useContext(LoadingContext);

  const {dispatchMessage} = useContext(SnackBarContext);

  const [surveySurvice] = useMemo(() => {
    return [new SurveyService()];
  }, []);

  const [title, setTitle] = useState<string>('');

  const surveyDetails = handleShowSurveyDetails && handleShowSurveyDetails(state);
  const isReadOnly = state === 'current' && !!surveyDetails;
  const isStateCurrent = state === 'current';
  const isUpdatingUpcomingSurvey = state === 'upcoming' && !!surveyDetails?.resourceId;
  const defaultStartDate = !isReadOnly && !surveyDetails?.startsAt ? formatSurveyDate(dayjs()) : '';

  useEffect(() => {
    const handleTitle = () => {
      switch (type) {
        case 'happiness':
          return t('companySurveys.stats.happinessSurvey');
        case 'culture':
          return t('companySurveys.stats.cultureSurvey');
        default:
          return '';
      }
    };
    setTitle(handleTitle());
  }, [type, t]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      surveyType: type,
      defaultLanguage: surveyDetails?.defaultLanguage ?? 'de',
      autoClose: surveyDetails?.autoClose ?? false,
      reccuranceType: surveyDetails?.recurrenceType ?? '',
      startsAt: surveyDetails?.startsAt ?? defaultStartDate,
      endsAt: surveyDetails?.endsAt ?? '',
      expectedParticipants: surveyDetails?.expectedParticipants ?? MINIMUM_EXPECTED_PARTICIPANTS,
      omitDepartment: surveyDetails?.configuration?.omitDepartment ?? false,
      omitSpokenLanguage: surveyDetails?.configuration?.omitSpokenLanguage ?? false,
      omitGender: surveyDetails?.configuration?.omitGender ?? false,
      omitLocation: surveyDetails?.configuration?.omitLocation ?? false,
      omitAgeGroup: surveyDetails?.configuration?.omitAgeGroup ?? false,
      omitWorkMode: surveyDetails?.configuration?.omitWorkMode ?? false,
      omitFeedback: surveyDetails?.configuration?.omitFeedback ?? false,
    },
    onSubmit: (values) => {
      setLoadingContext(true);
      const payload: StartSurveyPayload = {
        companySurvey: {
          surveyType: values.surveyType,
          defaultLanguage: values.defaultLanguage || 'de',
          autoClose: false,
          reccuranceType: values.reccuranceType || '',
          startsAt: values.startsAt || '',
          endsAt: values.endsAt || '',
          expectedParticipants: MINIMUM_EXPECTED_PARTICIPANTS,
          omitDepartment: values.omitDepartment,
          omitSpokenLanguage: values.omitSpokenLanguage,
          omitGender: values.omitGender,
          omitLocation: values.omitLocation,
          omitAgeGroup: values.omitAgeGroup,
          omitWorkMode: values.omitWorkMode,
          omitFeedback: values.omitFeedback,
        },
      };

      submitForm(payload);
    },

    validate: (values) => {
      const errors: any = {};

      if (!values.defaultLanguage) {
        errors.defaultLanguage = t('shared.required');
      }

      if (!values.startsAt) {
        errors.startsAt = t('shared.required');
      }

      if (!values.endsAt) {
        errors.endsAt = t('shared.required');
      }

      return errors;
    },
  });

  const submitForm = async (values: StartSurveyPayload) => {
    const res =
      isUpdatingUpcomingSurvey && surveyDetails?.resourceId
        ? await surveySurvice.updateSurvey(companyId, surveyDetails.resourceId, values)
        : await surveySurvice.startSurvey(companyId, values);

    if (res && res.ok) {
      dispatchMessage(
        isUpdatingUpcomingSurvey ? t('shared.formSubmit.success') : t('shared.formSubmit.creationSuccess'),
        'success'
      );
      handleClose();
      fetchSurveys();
    } else {
      if (res.error?.extra_details.includes('overlaps with existing survey')) {
        dispatchMessage(t('companySurveys.modals.startEndDateOverlapError'), 'error');
      } else if (res.error?.extra_details.includes(SURVEY_START_API_ERRORS.noLocations)) {
        dispatchMessage(t('companySurveys.modals.companyLocationsError'), 'error');
      } else if (res.error?.extra_details.includes(SURVEY_START_API_ERRORS.noDepartments)) {
        dispatchMessage(t('companySurveys.modals.companyDepartmentsError'), 'error');
      } else if (res.error?.extra_details.includes(SURVEY_START_API_ERRORS.noBenefits)) {
        dispatchMessage(t('companySurveys.modals.companyBenefitsError'), 'error');
      } else {
        dispatchMessage(t('verification.errorMessage'), 'error');
      }
    }
    setLoadingContext(false);
  };

  const handleEmpionLogo = () => {
    return (
      <EmpionLogo
        fill={'white'}
        aria-label='empion logo'
        style={{
          marginRight: '4px',
          marginBottom: '2px',
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        borderRadius: '16px',
        padding: {xs: '1rem', sm: '2rem'},
      }}
    >
      <ModalHeader title={title} handleClose={handleClose} />
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <SurveyOptions formik={formik} isReadOnly={!!isReadOnly} />
          <Calendar formik={formik} isReadOnly={!!isReadOnly} syncDatesFromFormik={!!surveyDetails} />
          {!isStateCurrent && (
            <EmpionButton
              type='submit'
              hoverBackgroundColor={greenColors[500]}
              sx={{
                mt: 3,
                backgroundColor: greenColors[700],
              }}
            >
              {handleEmpionLogo()}
              {isUpdatingUpcomingSurvey ? t('shared.formButtons.save') : t('companySurveys.actionButtons.start')}
            </EmpionButton>
          )}
        </form>
      </FormikProvider>
    </Box>
  );
};
